import React, { useEffect, useState, useCallback } from 'react'
import {
  Button,
  Text,
  Input,
  Select,
  CurrencyInput,
  Optins,
  ValuesContainer,
} from 'components'
import mask from 'utils/masks'
import { alternativeFormater } from 'utils/formatNumber'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import noop from 'lib/noop'
import emgeaIcon from 'assets/svgs/emgeaGreenIcon.svg'
import pagimovelClientes from 'assets/images/pagimovel-clientes.png'
import pagimovelCorretores from 'assets/images/pagimovel-corretores.png'
import selectPhoneMask from 'utils/selectPhoneMask'
import {
  validateEmail,
  validateFullName,
  validateCurrency,
  validateEntry,
  validateMandatoryPhone,
} from 'utils/validations'
import { boolean } from '@storybook/addon-knobs'
import SimulateValues from './simulateValues'
import styles from './SimulatorForm.module.scss'

const SimulatorForm = ({
  values,
  setField,
  sendForm,
  setTouched,
  touched,
  handleClose,
  loading,
  noModal,
}) => {
  const {
    name,
    email,
    phone,
    balance_amount,
    entrance_value,
    first_parcel_amount,
    last_parcel_amount,
    parcel_quantity,
  } = values
  const [checkOffers, setCheckOffers] = useState(true)
  const [checkOffersResale, setCheckOffersResale] = useState(true)
  const monthlyInterest = 0.643403011000343
  const yearlyInterest = Math.ceil(12 * monthlyInterest)

  const onCurrencyInputChange = (id, value) => {
    setTouched({ ...touched, [id]: false })
    setField(id, value)
  }

  const handleErrorMessage = (value, errorMessage) =>
    value === '' ? 'Campo obrigatório' : errorMessage

  const handleBlur = field => () => setTouched({ ...touched, [field]: true })

  const onChange = e => {
    setTouched({ ...touched, [e.target.id]: false })
    setField(e.target.id, e.target.value)
  }
  const callSendForm = async formValues => {
    setTouched({})
    await sendForm(formValues)
  }

  const options = [
    { value: '', name: 'Selecione' },
    { value: 12, name: '12' },
    { value: 24, name: '24' },
    { value: 36, name: '36' },
    { value: 48, name: '48' },
    { value: 60, name: '60' },
    { value: 72, name: '72' },
    { value: 84, name: '84' },
    { value: 96, name: '96' },
    { value: 108, name: '108' },
    { value: 120, name: '120' },
  ]

  const isDisabled =
    !validateEmail(email) ||
    !validateFullName(name) ||
    !validateMandatoryPhone(phone) ||
    !validateCurrency(balance_amount) ||
    !validateEntry(balance_amount, entrance_value) ||
    parcel_quantity === '' ||
    !checkOffersResale ||
    !checkOffers

  const valuesSection = [
    {
      state: alternativeFormater(balance_amount),
      content: `Valor do imóvel`,
      className: '',
      format: true,
    },
    {
      state: alternativeFormater(balance_amount - (entrance_value || 0)),
      content: `Valor parcelado`,
      className: '',
      format: true,
    },
    {
      state: true,
      content: <hr />,
      className: 'hrbar',
      format: false,
    },
    {
      state: alternativeFormater(entrance_value || 0),
      content: `Valor de entrada`,
      className: 'highlighted',
      format: true,
    },
    {
      state: parcel_quantity && `${parcel_quantity}x`,
      content: `Parcelado em`,
      className: 'highlighted',
      format: false,
    },
    {
      state: first_parcel_amount,
      content: `Primeira parcela`,
      className: 'highlighted',
      format: true,
    },
    {
      state: last_parcel_amount,
      content: `Última parcela (estimada, sem correção)`,
      className: 'highlighted',
      format: true,
    },
    {
      state: `${yearlyInterest
        .toFixed(2)
        .toString()
        .replace('.', ',')}%`,
      content: `Taxa de juros anual (nominal)`,
      className: 'interest',
      format: false,
    },
    {
      state: `${monthlyInterest
        .toFixed(2)
        .toString()
        .replace('.', ',')}%`,
      content: `Taxa de juros mensal (efetiva)`,
      className: 'interest',
      format: false,
    },
    {
      state: true,
      content: `Sistema de amortização: SACRE`,
      className: 'footNote',
      format: false,
    },
    {
      state: true,
      content: `Correção monetária: POUPANÇA`,
      className: 'footNote',
      format: false,
    },
  ]

  const simulateValues = useCallback(simulationParams => {
    return SimulateValues(simulationParams)
  }, [])

  useEffect(() => {
    simulateValues({
      priceValue: balance_amount,
      entryValue: entrance_value,
      monthlyInterestValue: monthlyInterest,
      installmentsValue: parcel_quantity,
      setterFirstInstallment: setField,
      setterLastInstallment: setField,
    })
    // eslint-disable-next-line
  }, [
    entrance_value,
    balance_amount,
    monthlyInterest,
    parcel_quantity,
    simulateValues,
    first_parcel_amount,
    last_parcel_amount,
  ])

  return (
    <section
      className={classNames(styles.simulatorContainer, {
        [styles.noModal]: noModal,
      })}
    >
      {!noModal && (
        <header
          className={classNames(styles.header, { [styles.noModal]: noModal })}
        >
          <div className={styles.top}>
            <Text fontSize="regular" color="grayColor" className={styles.title}>
              Parcelamento Direto
            </Text>
            <figure>
              <img
                loading="lazy"
                src={emgeaIcon}
                alt="Ícone da logo do vendedor Emgea"
              />
            </figure>
          </div>
        </header>
      )}
      <main className={styles.main}>
        <Text
          fontSize="medium"
          color="blackColor"
          className={styles.contactText}
          bold
        >
          Opções de parcelamento
        </Text>
        <CurrencyInput
          errorMessage={handleErrorMessage(balance_amount, 'Preço inválido')}
          onBlur={handleBlur('balance_amount')}
          hasError={!validateCurrency(balance_amount) && touched.balance_amount}
          id="balance_amount"
          name="balance_amount"
          label="Valor do imóvel"
          placeholder="R$"
          className={styles.inputName}
          labelClassName={styles.modalLabel}
          value={balance_amount}
          onChange={onCurrencyInputChange}
        />
        <CurrencyInput
          errorMessage={handleErrorMessage(
            entrance_value,
            'Corrigir o valor da entrada para, no mínimo, 10% do valor do imóvel.'
          )}
          onBlur={handleBlur('entrance_value')}
          hasError={
            !validateEntry(balance_amount, entrance_value) &&
            touched.entrance_value
          }
          id="entrance_value"
          name="entrance_value"
          label="Valor de entrada R$ (mínimo de 10% do valor do imóvel)"
          placeholder="R$"
          className={styles.inputName}
          labelClassName={styles.modalLabel}
          value={entrance_value}
          onChange={onCurrencyInputChange}
        />

        <Select
          errorMessage="Selecione um número de parcelas"
          onBlur={handleBlur('parcel_quantity')}
          hasError={parcel_quantity === '' && touched.parcel_quantity}
          id="parcel_quantity"
          onChange={onChange}
          value={parcel_quantity}
          options={options}
          className={styles.inputSubject}
          label="Número de parcelas"
        />

        {balance_amount && <ValuesContainer valuesSection={valuesSection} />}

        {!noModal && (
          <>
            {' '}
            <div className={styles.info}>
              <Text
                fontSize="medium"
                color="grayColor"
                className={styles.contactText}
                bold
              >
                Dados pessoais
              </Text>
            </div>
            <Input
              errorMessage={handleErrorMessage(name, 'Nome completo inválido')}
              onBlur={handleBlur('name')}
              hasError={!validateFullName(name) && touched.name}
              id="name"
              label="Nome completo*"
              placeholder=""
              className={styles.inputName}
              labelClassName={styles.modalLabel}
              onChange={onChange}
              value={name}
            />
            <Input
              errorMessage={handleErrorMessage(phone, 'Telefone inválido')}
              onBlur={handleBlur('phone')}
              hasError={!validateMandatoryPhone(phone) && touched.phone}
              mask={selectPhoneMask(phone, mask)}
              id="phone"
              label="Telefone*"
              placeholder=""
              className={styles.inputEmail}
              labelClassName={styles.modalLabel}
              onChange={onChange}
              value={phone}
            />
            <Input
              errorMessage={handleErrorMessage(email, 'Email inválido')}
              onBlur={handleBlur('email')}
              hasError={!validateEmail(email) && touched.email}
              id="email"
              label="E-mail*"
              type="email"
              placeholder=""
              className={styles.inputEmail}
              labelClassName={styles.modalLabel}
              onChange={onChange}
              value={email}
            />
          </>
        )}
        <Text
          fontSize="small"
          color="blackColor"
          className={styles.warnMessage}
        >
          <br />
          <strong>IMPORTANTE</strong>: Os resultados apresentados são meramente
          para efeitos de simulação e a aprovação do crédito está sujeita à
          análise do vendedor. A correção monetária das parcelas e do saldo
          devedor será realizada anualmente pelo índice mensal de remuneração
          básica dos depósitos em caderneta de poupança.
        </Text>
        {!noModal && (
          <div className={styles.optins}>
            <Optins
              checkOffers={checkOffers}
              setCheckOffers={setCheckOffers}
              checkOffersResale={checkOffersResale}
              setCheckOffersResale={setCheckOffersResale}
            />
          </div>
        )}
        {noModal && (
          <div className={styles.pagimovelContainer}>
            <Text className={styles.pagimovelTitle}>
              Para retornar a sua proposta acesse o Pagimovel
            </Text>
            <div>
              <a
                href="https://clientes.pagimovel.com.br/welcome/login"
                target="blank"
              >
                <img
                  loading="lazy"
                  src={pagimovelClientes}
                  alt="Pagimovel Clientes"
                />
              </a>
              <a
                href="https://corretores.pagimovel.com.br/welcome/login"
                target="blank"
              >
                <img
                  loading="lazy"
                  src={pagimovelCorretores}
                  alt="Pagimovel Corretores"
                />
              </a>
            </div>
          </div>
        )}
      </main>
      {!noModal && (
        <footer
          className={classNames(styles.footer, { [styles.noModal]: noModal })}
        >
          <div className={styles.buttonContainer}>
            <Button
              id="btn_criar_visita"
              type="filled"
              darkColor
              className={classNames(styles.button, {
                [styles.noModal]: noModal,
              })}
              fontSize="medium"
              whiteColor
              onClick={() => handleClose()}
            >
              Fechar
            </Button>
            <Button
              id="btn_criar_visita"
              type="filled"
              redColor
              className={styles.button}
              fontSize="medium"
              disabled={isDisabled || loading}
              onClick={() => callSendForm(values)}
              isLoading={loading}
            >
              Enviar por e-mail
            </Button>
          </div>
        </footer>
      )}
    </section>
  )
}

SimulatorForm.propTypes = {
  setTouched: PropTypes.func.isRequired,
  touched: PropTypes.objectOf(boolean).isRequired,
  handleClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setField: PropTypes.func,
  values: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    balance_amount: PropTypes.string.isRequired,
    entrance_value: PropTypes.string.isRequired,
    parcel_quantity: PropTypes.string.isRequired,
    first_parcel_amount: PropTypes.string,
    last_parcel_amount: PropTypes.string,
  }),
  sendForm: PropTypes.func,
  noModal: PropTypes.bool,
}

SimulatorForm.defaultProps = {
  sendForm: noop,
  setField: noop,
  noModal: false,
  values: {
    first_parcel_amount: '',
    last_parcel_amount: '',
  },
}
export default React.memo(SimulatorForm)
